import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { buildProxyAuthUrl } from '../utils/proxyUrls';
import { getApiUrl } from '../utils/api';

/**
 * Standalone proxy page for opening Streamlit/Bokeh/etc. apps in a dedicated browser window.
 * Fetches its own fresh auth code on mount — the code passed in the URL may already
 * be consumed by the embedded iframe or expired (60s TTL, single-use).
 */
const StandaloneProxyPage: React.FC = () => {
  const location = useLocation();
  const [params, setParams] = useState<{
    sessionId: string;
    port: string;
  } | null>(null);
  const [proxyUrl, setProxyUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const sessionId = sp.get('sessionId');
    const port = sp.get('port');
    if (sessionId && port) {
      setParams({ sessionId, port });
    }
  }, [location.search]);

  useEffect(() => {
    if (!params) return;
    document.title = `App :${params.port} - ${params.sessionId.slice(0, 8)}`;

    const fetchFreshAuthCode = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Not authenticated — please log in and try again');
        return;
      }

      try {
        const res = await fetch(
          `${getApiUrl()}/api/v1/notebooks/proxy-auth/${params.sessionId}`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!res.ok) {
          setError(`Failed to get auth code (${res.status})`);
          return;
        }
        const data = await res.json();
        if (!data.auth_code) {
          setError('No auth code returned');
          return;
        }
        setProxyUrl(
          buildProxyAuthUrl(params.sessionId, data.auth_code, `/proxy/${params.port}/`),
        );
      } catch (err) {
        setError(`Auth request failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    };

    fetchFreshAuthCode();
  }, [params]);

  if (!params) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl font-bold mb-4">Invalid Proxy Session</h1>
          <p className="text-gray-400">Missing session ID or port</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl font-bold mb-4">Proxy Auth Error</h1>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setProxyUrl(null);
              // Re-trigger the fetch
              setParams(prev => prev ? { ...prev } : null);
            }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!proxyUrl) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-400">Authenticating proxy session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-black flex flex-col overflow-hidden">
      {/* Minimal header */}
      <div className="h-10 bg-black/80 backdrop-blur-sm border-b border-gray-800/50 flex items-center px-4 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse shadow-lg shadow-purple-400/50" />
          <span className="text-sm text-gray-300 font-medium">App Session</span>
          <span className="text-xs text-gray-500">:{params.port}</span>
          <span className="text-xs text-gray-500">{params.sessionId.slice(0, 8)}...</span>
        </div>
        <div className="ml-auto">
          <button
            onClick={() => {
              window.close();
              setTimeout(() => {
                window.location.href = '/dashboard/compute';
              }, 100);
            }}
            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-all rounded"
            title="Close Window"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Fullscreen iframe */}
      <iframe
        src={proxyUrl}
        className="flex-1 w-full border-none"
        title={`Proxy app on port ${params.port}`}
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
      />
    </div>
  );
};

export default StandaloneProxyPage;
